import { SearchClient } from 'algoliasearch';
import { SearchOptions, Hit } from '@algolia/client-search';
import { RequestOptions } from '@algolia/transporter';
import { getAlgoliaSearch } from '@state/utils/helper';
import { Logger } from '@core/logger';

export abstract class AlgoliaSearchService {
	protected algoliaSearchClient: SearchClient;
	protected indexName: string;

	/**
	 * Initialize the service with user credentials and the desired Algolia index name.
	 * This method sets up the Algolia client. Call this once you have the user and index name available.
	 */
	public initialize(algoliaSearchKey: string, indexName: string) {
		this.indexName = indexName;
		if (algoliaSearchKey) {
			this.algoliaSearchClient = getAlgoliaSearch(algoliaSearchKey);
		} else {
			throw new Error('No Algolia search key available for user.');
		}
	}

	/**
	 * Performs a raw search against the Algolia index.
	 *
	 * @param query - The search query string.
	 * @param requestOptions - Optional search parameters.
	 * @param filterFunction - Optional function to filter or transform results.
	 * @returns A Promise that resolves to an array of hits.
	 */
	public async searchAlgolia<T = unknown>(
		query: string,
		requestOptions?: RequestOptions & SearchOptions,
		filterFunction?: (items: Hit<T>[]) => Hit<T>[],
	): Promise<Hit<T>[]> {
		if (!this.algoliaSearchClient) {
			Logger.error(
				`Algolia search client not initialized for index ${this.indexName}`,
			);
			return [];
		}

		try {
			const index = this.algoliaSearchClient.initIndex(this.indexName);
			const response = await index.search<T>(query, requestOptions);
			const hits = response.hits;
			return filterFunction ? filterFunction(hits) : hits;
		} catch (error) {
			Logger.exception(error, {
				errorLocation: `Error searching for ${this.indexName}`,
			});
			throw error;
		}
	}

	/**
	 * If searchTerm is empty or null, it returns an empty array.
	 */
	public async search<T = unknown>(
		searchTerm: string,
		requestOptions?: RequestOptions & SearchOptions,
		filterFunction?: (items: Hit<T>[]) => Hit<T>[],
	): Promise<Hit<T>[]> {
		if (!searchTerm) {
			return [];
		}

		return this.searchAlgolia<T>(searchTerm, requestOptions, filterFunction);
	}
}
